import React, { useEffect } from 'react'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react'
import { FiRefreshCw } from 'react-icons/fi'
import { useMutation } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { monitoringService } from '@/services/admin/monitoringService'
import type { NetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import type {
  PlatformContractDescriptor,
  PlatformContractConsistencyResult,
  PlatformContractConsistencyStatus,
} from '@/types'
import { formatDateTime, truncateAddress } from '@/utils/formatUtils'

interface PlatformContractConsistencyModalProps {
  isOpen: boolean
  onClose: () => void
  mode: NetworkEnvironment
  targetContract?: PlatformContractDescriptor | null
}

const statusColor: Record<PlatformContractConsistencyStatus, string> = {
  pass: 'green',
  warning: 'yellow',
  fail: 'red',
}

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message
  }
  return 'Failed to check platform contracts'
}

const SummaryItem = ({
  label,
  value,
}: {
  label: string
  value: number | string
}) => {
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  return (
    <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" px={3} py={2}>
      <Text fontSize="xs" color="gray.500">
        {label}
      </Text>
      <Text fontWeight="semibold">{value}</Text>
    </Box>
  )
}

const ConsistencyResultPanel = ({
  result,
}: {
  result: PlatformContractConsistencyResult
}) => {
  const { t } = useTranslation()
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const hasIssues = result.issues.length > 0

  return (
    <VStack align="stretch" spacing={4}>
      <HStack justify="space-between" align="center">
        <HStack spacing={2}>
          <Badge colorScheme={statusColor[result.status]}>
            {result.status.toUpperCase()}
          </Badge>
          <Badge variant="outline">{result.mode}</Badge>
        </HStack>
        <Text fontSize="xs" color="gray.500">
          {formatDateTime(result.checkedAt)}
        </Text>
      </HStack>

      <SimpleGrid columns={{ base: 2, md: 3 }} spacing={3}>
        <SummaryItem
          label={t('monitoring.platformContracts.consistency.total', 'Total')}
          value={result.summary.totalContracts}
        />
        <SummaryItem
          label={t('monitoring.platformContracts.consistency.active', 'Active')}
          value={result.summary.activeContracts}
        />
        <SummaryItem
          label={t(
            'monitoring.platformContracts.consistency.activeBatch',
            'Active batch'
          )}
          value={result.summary.activeFaucetBatchMinters}
        />
        <SummaryItem
          label={t(
            'monitoring.platformContracts.consistency.requiredBatch',
            'Required batch'
          )}
          value={result.summary.requiredFaucetBatchMinters}
        />
        <SummaryItem
          label={t('monitoring.platformContracts.consistency.issues', 'Issues')}
          value={result.summary.issueCount}
        />
      </SimpleGrid>

      {!hasIssues ? (
        <Alert status="success" borderRadius="md">
          <AlertIcon />
          <AlertDescription>
            {t(
              'monitoring.platformContracts.consistency.noIssues',
              'No platform contract consistency issues were found.'
            )}
          </AlertDescription>
        </Alert>
      ) : (
        <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" overflowX="auto">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>{t('common.status', 'Status')}</Th>
                <Th>{t('monitoring.platformContracts.labels.chain', 'Chain')}</Th>
                <Th>{t('monitoring.platformContracts.labels.contract', 'Contract')}</Th>
                <Th>{t('monitoring.platformContracts.labels.address', 'Address')}</Th>
                <Th>{t('common.message', 'Message')}</Th>
              </Tr>
            </Thead>
            <Tbody>
              {result.issues.map((issue, index) => (
                <Tr key={`${issue.code}-${issue.chainId ?? 'global'}-${index}`}>
                  <Td>
                    <Badge colorScheme={issue.severity === 'fail' ? 'red' : 'yellow'}>
                      {issue.severity}
                    </Badge>
                  </Td>
                  <Td>{issue.chainCode ?? issue.chainId ?? '-'}</Td>
                  <Td>{issue.contractType ?? '-'}</Td>
                  <Td fontFamily="mono" fontSize="xs">
                    {issue.contractAddress ? truncateAddress(issue.contractAddress) : '-'}
                  </Td>
                  <Td>
                    <Text fontSize="sm">{issue.message}</Text>
                    <Text fontSize="xs" color="gray.500">
                      {issue.code}
                    </Text>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
    </VStack>
  )
}

export const PlatformContractConsistencyModal: React.FC<
  PlatformContractConsistencyModalProps
> = ({ isOpen, onClose, mode, targetContract }) => {
  const { t } = useTranslation()
  const mutation = useMutation({
    mutationFn: () => monitoringService.checkPlatformContractConsistency(
      mode,
      targetContract
        ? {
            chainId: targetContract.chainId,
            chainCode: targetContract.chainCode,
          }
        : undefined
    ),
  })
  const { reset } = mutation

  useEffect(() => {
    if (isOpen) {
      reset()
    }
  }, [isOpen, reset, targetContract?.id])

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {t(
            'monitoring.platformContracts.consistency.title',
            'Platform contract consistency'
          )}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={4}>
            <HStack justify="space-between" align={{ base: 'stretch', md: 'center' }}>
              <Box>
                <Text fontSize="sm" color="gray.500">
                  {t('monitoring.platformContracts.consistency.mode', 'Mode')}
                </Text>
                <Badge colorScheme={mode === 'testnet' ? 'purple' : 'green'}>
                  {mode}
                </Badge>
              </Box>
              {targetContract && (
                <Box>
                  <Text fontSize="sm" color="gray.500">
                    {t('monitoring.platformContracts.consistency.target', 'Target')}
                  </Text>
                  <Text fontSize="sm" fontWeight="semibold">
                    {targetContract.chainCode}
                  </Text>
                </Box>
              )}
              <Button
                leftIcon={<FiRefreshCw />}
                colorScheme="blue"
                onClick={() => mutation.mutate()}
                isLoading={mutation.isPending}
                alignSelf={{ base: 'stretch', md: 'auto' }}
              >
                {t('monitoring.platformContracts.consistency.run', 'Run check')}
              </Button>
            </HStack>

            {mutation.isError && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                <AlertDescription>{getErrorMessage(mutation.error)}</AlertDescription>
              </Alert>
            )}

            {mutation.data ? (
              <ConsistencyResultPanel result={mutation.data} />
            ) : (
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                <AlertDescription>
                  {t(
                    'monitoring.platformContracts.consistency.ready',
                    'Run a read-only check against the current platform contract registry.'
                  )}
                </AlertDescription>
              </Alert>
            )}
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.close', 'Close')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}

export default PlatformContractConsistencyModal
